/**
 * Redacting student names out of an upload BEFORE `submissionSplit` ever runs — one row per upload, same shape
 * as `submissionSplit` itself (see that job's own header: "NOT A FAN-OUT... one row, exam-scoped lock").
 *
 * RUNS FIRST, AND DISPATCHES `submissionSplit` ITSELF. There is no job-system primitive for "type A then type
 * B" beyond a `ready()` gate on exam state (see `grading` → `gradingConsistency`), which does not fit here:
 * this job's OUTPUT (the redacted file ids, the name mapping) is exactly what `submissionSplit`'s payload
 * needs, and nothing about exam state reflects that. So this handler dispatches `submissionSplit` itself,
 * directly, after its own work — the same pattern `answer-extraction.job.ts`'s handler already uses to queue
 * a re-grade after extraction succeeds.
 *
 * SHARES `uploadLockKey` WITH `submissionSplit` ON PURPOSE. Both are phases of the same upload; the shared
 * lock is what stops a split from ever running against an upload this job hasn't finished redacting yet —
 * `submissionSplit`'s own claim step simply waits for this row's lock to release, exactly as it already
 * would against a second concurrent split of the same exam.
 *
 * `submissionSplit`'s `dedupeKey` keys off `fileIds` directly, same as any other job — safe to do PLAINLY now
 * that `anonymizeSubmissionFiles` never creates a new redacted PDF (see its own header): `redactedFileIds` is
 * always just `payload.fileIds` itself, so a retry of THIS job reprocesses the exact same ids and produces the
 * exact same `fileIds` for `submissionSplit` every time — nothing here is non-deterministic across retries any
 * more, so there is no separate "stable id for deduping" concern left to solve.
 *
 * DELETES THE ORIGINAL, UN-REDACTED UPLOADS once anonymization has produced its replacement — not a transient
 * cleanup, the point of the whole pipeline: a real, unredacted student name must not go on existing in storage
 * after the one call that's allowed to see it has finished. See `anonymizeSubmissionFiles`'s own header. Only
 * an id that actually went through redaction (present as a key in `redactedImageIds`, PDFs and images alike) is
 * deleted — a passthrough upload (never redacted, never a `redactedImageIds` entry) is left alone, since its id
 * IS the one live file still in use, same id or not. Deletion runs after the `submissionSplit` dispatch attempt, not gated
 * on it succeeding: that dispatch is already non-fatal-on-failure by design (see the `.catch` below), and the
 * guarantee this satisfies is "anonymization happened", not "the split ran" — the two are already allowed to be
 * independent outcomes elsewhere in this job.
 */

import { logger } from '@adornis/base/logging.js';
import { anonymizeSubmissionFiles } from '../../agents/anonymize-submission-pages.js';
import { removeStoredFile } from '../../../utils/delete-cascade.js';
import { uploadLockKey } from '../job.js';
import { defineJob } from '../registry.js';

export interface AnonymizeSubmissionPagesPayload {
  fileIds: string[];
  conversationId?: string;
  guestUserID?: string;
  ownerUserID?: string;
  [key: string]: unknown;
}

export const anonymizeSubmissionPagesJob = defineJob<AnonymizeSubmissionPagesPayload>({
  type: 'anonymizeSubmissionPages',

  pool: 'heavy',

  batch: {
    units: (_examId, scope) => {
      const { fileIds = [], ...owners } = scope as {
        fileIds?: string[];
        conversationId?: string;
        guestUserID?: string;
        ownerUserID?: string;
      };
      return fileIds.length ? [{ fileIds, ...owners }] : [];
    },
  },

  lockKey: examId => uploadLockKey(examId),

  dedupeKey: (examId, payload) => `anonymizeSubmissionPages:${examId}:${[...payload.fileIds].sort().join(',')}`,

  ready: (_exam, job) => {
    if (job.payload.fileIds.length === 0) return { verdict: 'drop', because: 'anonymizeSubmissionPages:no-files' };
    return 'run';
  },

  // Comparable cost shape to submissionSplit: a model call per page, then one ImageFile persisted per page —
  // no PDF reassembly any more (see `anonymizeSubmissionFiles`'s own header).
  maxRuntimeMs: 30 * 60_000,

  // Safe to retry: this job never writes a Submission or anything else with retry-collision risk — a retry's
  // redacted images are, at worst, orphaned derived files, never a duplicate downstream cohort.
  maxAttempts: 3,

  handler: async (examId, payload) => {
    const { redactedFileIds, redactedImageIds, nameMapping } = await anonymizeSubmissionFiles(payload.fileIds, examId);

    const { dispatchJobs } = await import('../dispatch.js');
    await dispatchJobs('submissionSplit', examId, {
      fileIds: redactedFileIds,
      redactedImageIds,
      nameMapping,
      ...(payload.conversationId ? { conversationId: payload.conversationId } : {}),
      ...(payload.guestUserID ? { guestUserID: payload.guestUserID } : {}),
      ...(payload.ownerUserID ? { ownerUserID: payload.ownerUserID } : {}),
    }).catch(err => logger.error({ err, examId, context: 'AnonymizeSubmission' }, 'Could not queue submissionSplit after anonymization'));

    // See this file's own header: only an id that actually went through redaction (has a `redactedImageIds`
    // entry) gets deleted — a passthrough upload never got one, and IS still the one live file in use.
    const deletable = payload.fileIds.filter(originalId => Object.prototype.hasOwnProperty.call(redactedImageIds, originalId));
    await Promise.all(
      deletable.map(originalId =>
        removeStoredFile(originalId).catch(err =>
          logger.error({ err, examId, originalId, context: 'AnonymizeSubmission' }, 'Could not delete original upload after redaction'),
        ),
      ),
    );

    return {
      note: `anonymized ${redactedFileIds.length} file(s), ${nameMapping.filter(m => m.transcribedName).length} named page(s), deleted ${deletable.length} original upload(s)`,
    };
  },
});
